import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Business,
  Delete,
  Download,
  Edit,
  Language,
  Person,
  Phone,
  Visibility,
  WhatsApp,
} from '@mui/icons-material';
import { STATUS_COLORS } from '../../Components/ContactForm';
import CopyButton from '../../Components/CopyButton';
import {
  ACTIVITY_OUTCOMES,
  CONSENT_OPTIONS,
  LEAD_TEMPERATURE_OPTIONS,
  PROFESSIONALISM_OPTIONS,
  QUALITY_FIELDS,
  RED_FLAGS,
  RESPONSIVENESS_OPTIONS,
  getFollowUpState,
  getLastActivity,
  getQuality,
  getReadiness,
} from '../../Components/ContactActivity';
import ContactFilterBar from './ContactFilterBar';
import {
  DEFAULT_FILTERS,
  contactEmail,
  contactName,
  contactPhone,
  contactTags,
  contactWhatsapp,
  countActiveFilters,
  loadSavedFilters,
  matchesFilters,
  saveFilters,
} from './contactFilters';

const TYPE_STYLE = {
  person: { color: '#2a78d6', icon: <Person sx={{ fontSize: 18 }} />, label: 'Person' },
  company: { color: '#eb6834', icon: <Business sx={{ fontSize: 18 }} />, label: 'Company' },
};


const isCompany = (contact) => contact.contactType === 'company';
export const getContactName = contactName;
export const getContactEmail = contactEmail;
const getContactPhone = contactPhone;
const getWhatsapp = contactWhatsapp;
const getTags = contactTags;

// Website as typed (may lack protocol) -> clickable URL and a short display label
const websiteHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
const websiteLabel = (url) => url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');

// Second line under the name: role for people, industry for companies
const getSubtitle = (contact) => {
  if (isCompany(contact)) return [contact.industry, contact.companySize].filter(Boolean).join(' · ');
  const role = [contact.jobTitle, contact.employer].filter(Boolean).join(' at ');
  return role;
};

const getInitials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

const formatRelative = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return '—';
  const diffMins = Math.floor((Date.now() - date) / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const SORTERS = {
  name: (c) => getContactName(c).toLowerCase(),
  city: (c) => (c.city || '').toLowerCase(),
  contactStatus: (c) => (c.contactStatus || '').toLowerCase(),
  // No follow-up sorts last in ascending order
  nextFollowUpAt: (c) => (c.nextFollowUpAt ? new Date(c.nextFollowUpAt).getTime() : Number.MAX_SAFE_INTEGER),
  createdAt: (c) => new Date(c.createdAt).getTime() || 0,
};

const optionLabel = (options, value) => options.find((o) => o.value === value)?.label || '';

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const CSV_COLUMNS = [
  ['Contact ID', (c) => c.contactId],
  ['Type', (c) => c.contactType],
  ['Name', getContactName],
  ['Email', getContactEmail],
  ['Phone', getContactPhone],
  ['Website', (c) => c.website],
  ['WhatsApp', getWhatsapp],
  ['Job Title', (c) => c.jobTitle],
  ['Employer', (c) => c.employer],
  ['Industry', (c) => c.industry || c.personIndustry],
  ['Company Size', (c) => c.companySize],
  ['GST Number', (c) => c.gstNumber],
  ['Status', (c) => c.contactStatus],
  ['Source', (c) => c.leadSource],
  ['City', (c) => c.city],
  ['State', (c) => c.state],
  ['Country', (c) => c.country],
  ['Tags', (c) => getTags(c).join('; ')],
  [
    'Education',
    (c) =>
      (c.education || [])
        .map((e) => [e.level, e.course, e.institution, e.currentlyStudying ? 'ongoing' : e.endYear].filter(Boolean).join(' - '))
        .join('; '),
  ],
  ['Marketing Readiness', (c) => getReadiness(c).label],
  ...QUALITY_FIELDS.map((field) => [
    `${field.label} Status`,
    (c) => field.options.find((o) => o.value === getQuality(c)[field.key])?.label || '',
  ]),
  ['Marketing Consent', (c) => optionLabel(CONSENT_OPTIONS, getQuality(c).marketingConsent)],
  ['Lead Temperature', (c) => optionLabel(LEAD_TEMPERATURE_OPTIONS, getQuality(c).leadTemperature)],
  ['Responsiveness', (c) => optionLabel(RESPONSIVENESS_OPTIONS, getQuality(c).responsiveness)],
  ['Professionalism', (c) => optionLabel(PROFESSIONALISM_OPTIONS, getQuality(c).professionalism)],
  ['Data Quality Rating', (c) => getQuality(c).rating || ''],
  ['Best Channel', (c) => getQuality(c).preferredChannel],
  ['Best Time', (c) => getQuality(c).bestTimeToContact],
  ['Interested In', (c) => getQuality(c).interests],
  ['Red Flags', (c) => getQuality(c).redFlags.map((f) => optionLabel(RED_FLAGS, f)).join('; ')],
  ['Next Follow-up', (c) => (c.nextFollowUpAt ? new Date(c.nextFollowUpAt).toISOString().slice(0, 10) : '')],
  ['Activities', (c) => (Array.isArray(c.activities) ? c.activities.length : 0)],
  ['Last Activity Date', (c) => (getLastActivity(c) ? new Date(getLastActivity(c).createdAt).toISOString() : '')],
  ['Last Activity', (c) => getLastActivity(c)?.text || ''],
  ['Last Outcome', (c) => optionLabel(ACTIVITY_OUTCOMES, getLastActivity(c)?.outcome)],
  ['Added On', (c) => (c.createdAt ? new Date(c.createdAt).toISOString() : '')],
];

const headCellSx = {
  fontWeight: 700,
  color: '#475569',
  fontSize: '0.75rem',
  py: 1.5,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  bgcolor: '#f8fafc',
  borderBottom: '2px solid #e2e8f0',
  whiteSpace: 'nowrap',
};

const cellSx = { py: 1.25, px: 2, fontSize: '0.875rem', color: '#64748b', borderBottom: '1px solid #f1f5f9' };

const ContactTable = ({ contacts, monthLabel, country, onCountryChange, onView, onEdit, onDelete, deletingId }) => {
  const [filters, setFilters] = useState(loadSavedFilters);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Remember filters for this browser tab so they survive opening a contact and coming back
  useEffect(() => saveFilters(filters), [filters]);

  const filtered = useMemo(() => {
    const matches = contacts.filter((contact) => matchesFilters(contact, filters));
    const getKey = SORTERS[sortBy];
    const direction = sortOrder === 'asc' ? 1 : -1;
    return matches.sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * direction;
      if (ka > kb) return 1 * direction;
      return 0;
    });
  }, [contacts, filters, sortBy, sortOrder]);

  // Back to the first page whenever the result set changes
  useEffect(() => setPage(0), [filters, contacts]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasFilters = countActiveFilters(filters) > 0;

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const clearFilters = () => setFilters({ ...DEFAULT_FILTERS });

  const exportCsv = () => {
    const rows = [CSV_COLUMNS.map(([header]) => header), ...filtered.map((c) => CSV_COLUMNS.map(([, get]) => get(c)))];
    const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `contacts-${(monthLabel || 'export').replace(/\s+/g, '-').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const sortableHead = (column, label) => (
    <TableSortLabel
      active={sortBy === column}
      direction={sortBy === column ? sortOrder : 'asc'}
      onClick={() => handleSort(column)}
    >
      {label}
    </TableSortLabel>
  );

  const stop = (handler) => (event) => {
    event.stopPropagation();
    handler?.();
  };

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Contact Directory
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {contacts.length} contact{contacts.length === 1 ? '' : 's'} in {monthLabel || 'this month'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Download />}
          onClick={exportCsv}
          disabled={filtered.length === 0}
          sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#475569' }}
        >
          Export CSV
        </Button>
      </Stack>

      <ContactFilterBar
        contacts={contacts}
        filters={filters}
        onChange={setFilters}
        country={country}
        onCountryChange={onCountryChange}
        resultCount={filtered.length}
      />

      <TableContainer sx={{ border: '1px solid #f1f5f9', borderRadius: 2, maxHeight: 640 }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, minWidth: 260 }}>{sortableHead('name', 'Contact')}</TableCell>
              <TableCell sx={headCellSx}>Contact ID</TableCell>
              <TableCell sx={headCellSx}>Phone</TableCell>
              <TableCell sx={headCellSx}>Website</TableCell>
              <TableCell sx={headCellSx}>{sortableHead('city', 'Location')}</TableCell>
              <TableCell sx={headCellSx}>{sortableHead('contactStatus', 'Status')}</TableCell>
              <TableCell sx={headCellSx}>{sortableHead('nextFollowUpAt', 'Follow-up')}</TableCell>
              <TableCell sx={headCellSx}>Tags</TableCell>
              <TableCell sx={headCellSx}>{sortableHead('createdAt', 'Added')}</TableCell>
              <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: hasFilters ? 1 : 0 }}>
                    {contacts.length === 0 ? `No contacts added in ${monthLabel || 'this month'}` : 'No contacts match your filters'}
                  </Typography>
                  {hasFilters && (
                    <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                      Clear filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((contact, index) => {
                const type = TYPE_STYLE[contact.contactType] || TYPE_STYLE.person;
                const name = getContactName(contact);
                const email = getContactEmail(contact);
                const subtitle = getSubtitle(contact);
                const phone = getContactPhone(contact);
                const whatsapp = getWhatsapp(contact);
                const tags = getTags(contact);
                const location = [contact.city, contact.state].filter(Boolean).join(', ');
                const website = (contact.website || '').trim();
                const statusColor = STATUS_COLORS[contact.contactStatus] || '#94a3b8';
                const readiness = getReadiness(contact);
                const followUp = getFollowUpState(contact.nextFollowUpAt);
                const lastActivity = getLastActivity(contact);
                const isDeleting = deletingId && deletingId === contact.dateTimeStamp;

                return (
                  <TableRow
                    key={contact.dateTimeStamp || index}
                    hover
                    onClick={() => {
                      // Let people drag-select text in a row without opening the contact
                      if (window.getSelection()?.toString()) return;
                      onView(contact);
                    }}
                    sx={{ cursor: 'pointer', '&:hover .row-actions': { opacity: 1 } }}
                  >
                    <TableCell sx={cellSx}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Tooltip title={type.label}>
                          <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700, bgcolor: `${type.color}1a`, color: type.color }}>
                            {isCompany(contact) ? type.icon : getInitials(name)}
                          </Avatar>
                        </Tooltip>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }} noWrap>
                            {name || 'Unnamed contact'}
                          </Typography>
                          {subtitle && (
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }} noWrap>
                              {subtitle}
                            </Typography>
                          )}
                          {email ? (
                            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: subtitle ? '#94a3b8' : '#64748b' }} noWrap>
                                {email}
                              </Typography>
                              <CopyButton value={email} label="Copy email" size={13} />
                            </Stack>
                          ) : (
                            !subtitle && (
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>—</Typography>
                            )
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: '#3b82f6', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {contact.contactId ? (
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <span>{contact.contactId}</span>
                          <CopyButton value={contact.contactId} label="Copy contact ID" size={13} />
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {phone ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Phone sx={{ fontSize: 15, color: '#94a3b8' }} />
                          <span>{phone}</span>
                          <CopyButton value={phone} label="Copy phone" size={13} />
                          {whatsapp && (
                            <Tooltip title={`WhatsApp ${whatsapp}`}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ color: '#25D366', p: 0.25 }}
                              >
                                <WhatsApp sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                      {website ? (
                        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
                          <Tooltip title={`Open ${websiteHref(website)}`}>
                            <Box
                              component="a"
                              href={websiteHref(website)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                                color: '#2563eb',
                                fontWeight: 500,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <Language sx={{ fontSize: 15, flexShrink: 0 }} />
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {websiteLabel(website)}
                              </Box>
                            </Box>
                          </Tooltip>
                          <CopyButton value={website} label="Copy website" size={13} />
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" sx={{ color: '#475569' }} noWrap>
                        {location || '—'}
                      </Typography>
                      {contact.country && (
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {contact.country}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        {contact.contactStatus ? (
                          <Chip
                            size="small"
                            label={contact.contactStatus}
                            sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: `${statusColor}1a`, color: '#1e293b', '& .MuiChip-label': { px: 1 } }}
                            icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor, ml: '8px !important' }} />}
                          />
                        ) : (
                          <span>—</span>
                        )}
                        <Tooltip title={`Marketing: ${readiness.label}`}>
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: readiness.color }} />
                            {readiness.short}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {followUp ? (
                        <Typography variant="body2" sx={{ color: followUp.color, fontWeight: followUp.diffDays <= 0 ? 700 : 500 }}>
                          {followUp.label}
                        </Typography>
                      ) : (
                        <span>—</span>
                      )}
                      {lastActivity && (
                        <Tooltip title={lastActivity.text}>
                          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                            Last: {formatRelative(lastActivity.createdAt)}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {tags.length ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {tags.slice(0, 2).map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.72rem', borderColor: '#e2e8f0', color: '#475569', maxWidth: 110 }} />
                          ))}
                          {tags.length > 2 && (
                            <Tooltip title={tags.slice(2).join(', ')}>
                              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                +{tags.length - 2}
                              </Typography>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      <Tooltip title={isNaN(new Date(contact.createdAt)) ? '' : new Date(contact.createdAt).toLocaleString()}>
                        <span>{formatRelative(contact.createdAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end" className="row-actions" sx={{ opacity: { xs: 1, md: 0.6 }, transition: 'opacity 0.15s' }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={stop(() => onView(contact))} sx={{ color: '#64748b', '&:hover': { color: '#3b82f6' } }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={stop(() => onEdit(contact))} sx={{ color: '#64748b', '&:hover': { color: '#3b82f6' } }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => onDelete(e, contact)}
                              disabled={Boolean(deletingId)}
                              sx={{ color: '#64748b', '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' } }}
                            >
                              {isDeleting ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(e, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{ borderTop: 'none', '.MuiTablePagination-toolbar': { px: 0 } }}
      />
    </Paper>
  );
};

export default ContactTable;
